import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ImagePlus, Trash2, RefreshCw, Check, X, Gem, Coins } from "lucide-react";
import { cn } from "@/lib/utils";

interface Avatar {
  id: number;
  name: string;
  imageUrl: string;
  category: string;
  isActive: boolean;
}

interface Banner {
  id: number;
  name: string;
  imageUrl: string;
  category: string;
  isActive: boolean;
  shopItemId: number | null;
}

interface ShopItem {
  id: number;
  name: string;
  description: string;
  imageUrl?: string | null;
  price: number;
  currencyType: "regular" | "premium";
  itemType: string;
  category: string;
  isActive: boolean;
  stock: number | null;
}

export function CustomizationTab() {
  const [activeTab, setActiveTab] = useState("avatars");
  const [isAvatarDialogOpen, setIsAvatarDialogOpen] = useState(false);
  const [isBannerDialogOpen, setIsBannerDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [avatarFormData, setAvatarFormData] = useState({
    name: "",
    category: "basic",
    imageFile: null as File | null,
    isActive: true,
  });
  const [bannerFormData, setBannerFormData] = useState({
    name: "",
    category: "basic",
    imageFile: null as File | null,
    isActive: true,
    shopItemId: "",
  });

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<{
    type: "avatar" | "banner";
    item: Avatar | Banner;
  } | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Queries
  const avatarsQuery = useQuery<Avatar[]>({
    queryKey: ["/api/customization/avatars"],
    queryFn: () => apiRequest("/api/customization/avatars"),
  });

  const bannersQuery = useQuery<Banner[]>({
    queryKey: ["/api/customization/banners"],
    queryFn: () => apiRequest("/api/customization/banners"),
  });

  // Replace the shop items query with separate queries for avatars and banners
  const { data: shopAvatars } = useQuery<ShopItem[]>({
    queryKey: ["/api/customization/shopAvatars"],
    queryFn: () => apiRequest("/api/customization/shopAvatars"),
  });

  const { data: shopBanners } = useQuery<ShopItem[]>({
    queryKey: ["/api/customization/shopBanners"],
    queryFn: () => apiRequest("/api/customization/shopBanners"),
  });

  // Mutations
  const createAvatarMutation = useMutation({
    mutationFn: async (data: FormData) => {
      return apiRequest("/api/customization/avatars/create", {
        method: "POST",
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/customization/avatars"] });
      toast({ title: "Avatar created successfully" });
      setIsAvatarDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error creating avatar",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const createBannerMutation = useMutation({
    mutationFn: async (data: FormData) => {
      return apiRequest("/api/customization/banners/create", {
        method: "POST",
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/customization/banners"] });
      toast({ title: "Banner created successfully" });
      setIsBannerDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error creating banner",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async ({ type, id }: { type: "avatar" | "banner"; id: number }) => {
      return apiRequest(`/api/customization/${type}s/delete/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: [
          `/api/customization/${selectedItem?.type === "avatar" ? "avatars" : "banners"}`
        ] 
      });
      toast({ title: "Item deleted successfully" });
      setIsDeleteDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting item",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Add edit mutation
  const editItemMutation = useMutation({
    mutationFn: async ({ type, id, data }: { type: "avatar" | "banner"; id: number; data: FormData }) => {
      return apiRequest(`/api/customization/${type}s/update/${id}`, {
        method: "PUT",
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: [
          `/api/customization/${editingItem?.type === "avatar" ? "avatars" : "banners"}`
        ] 
      });
      toast({ title: "Item updated successfully" });
      setIsEditDialogOpen(false);
      setEditingItem(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error updating item",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "avatar" | "banner"
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (type === "avatar") {
        setAvatarFormData((prev) => ({ ...prev, imageFile: file }));
      } else {
        setBannerFormData((prev) => ({ ...prev, imageFile: file }));
      }
    }
  };

  const handleCreateAvatar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!avatarFormData.imageFile) {
      toast({
        title: "Image required",
        description: "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", avatarFormData.name);
    formData.append("category", avatarFormData.category);
    formData.append("isActive", String(avatarFormData.isActive));
    formData.append("image", avatarFormData.imageFile);

    createAvatarMutation.mutate(formData);
  };

  const handleCreateBanner = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bannerFormData.imageFile) {
      toast({
        title: "Image required",
        description: "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", bannerFormData.name);
    formData.append("category", bannerFormData.category);
    formData.append("isActive", String(bannerFormData.isActive));
    formData.append("image", bannerFormData.imageFile);
    if (bannerFormData.shopItemId) {
      formData.append("shopItemId", bannerFormData.shopItemId);
    }

    createBannerMutation.mutate(formData);
  };

  const handleDelete = (type: "avatar" | "banner", id: number) => {
    setSelectedItem({ type, id });
    setIsDeleteDialogOpen(true);
  };

  const handleEdit = (type: "avatar" | "banner", item: Avatar | Banner) => {
    setEditingItem({ type, item });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;

    const formData = new FormData();
    const { type, item } = editingItem;

    if (type === "avatar") {
      const avatar = item as Avatar;
      formData.append("name", avatar.name);
      formData.append("category", avatar.category);
      formData.append("isActive", String(avatar.isActive));
      if (avatarFormData.imageFile) {
        formData.append("image", avatarFormData.imageFile);
      }
    } else {
      const banner = item as Banner;
      formData.append("name", banner.name);
      formData.append("category", banner.category);
      formData.append("isActive", String(banner.isActive));
      formData.append("shopItemId", banner.shopItemId?.toString() ?? "");
      if (bannerFormData.imageFile) {
        formData.append("image", bannerFormData.imageFile);
      }
    }

    editItemMutation.mutate({
      type: editingItem.type,
      id: editingItem.item.id,
      data: formData,
    });
  };

  const confirmDelete = () => {
    if (selectedItem) {
      deleteItemMutation.mutate({
        type: selectedItem.type,
        id: selectedItem.id,
      });
    }
  };

  if (avatarsQuery.isLoading || bannersQuery.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ["/api/customization/avatars"] });
            queryClient.invalidateQueries({ queryKey: ["/api/customization/banners"] });
            queryClient.invalidateQueries({ queryKey: ["/api/customization/shopAvatars"] });
            queryClient.invalidateQueries({ queryKey: ["/api/customization/shopBanners"] });
          }}
          className="h-8 w-8"
        >
          <RefreshCw className={cn("h-4 w-4", {
            "animate-spin": queryClient.isFetching()
          })} />
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="avatars">Free Avatars</TabsTrigger>
          <TabsTrigger value="shopAvatars">Shop Avatars</TabsTrigger>
          <TabsTrigger value="banners">Free Banners</TabsTrigger>
          <TabsTrigger value="shopBanners">Shop Banners</TabsTrigger>
        </TabsList>

        <TabsContent value="avatars">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-bold">Free Avatars</h2>
              <p className="text-sm text-muted-foreground">
                Manage available avatar options for users
              </p>
            </div>
            <Button onClick={() => setIsAvatarDialogOpen(true)}>
              <ImagePlus className="h-4 w-4 mr-2" />
              Add Avatar
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Preview</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {avatarsQuery.isSuccess && avatarsQuery.data?.map((avatar: Avatar) => (
                <TableRow key={avatar.id}>
                  <TableCell>
                    <img
                      src={avatar.imageUrl}
                      alt={avatar.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  </TableCell>
                  <TableCell>{avatar.name}</TableCell>
                  <TableCell>{avatar.category}</TableCell>
                  <TableCell>
                    {avatar.isActive ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                        <Check className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800">
                        <X className="h-3 w-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit("avatar", avatar)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete("avatar", avatar.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="shopAvatars">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-bold">Shop Avatars</h2>
              <p className="text-sm text-muted-foreground">
                Manage purchasable avatar items in the shop
              </p>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Preview</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shopAvatars?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                  </TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.price}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {item.currencyType === "premium" ? (
                        <Gem className="h-4 w-4 text-purple-500" />
                      ) : (
                        <Coins className="h-4 w-4 text-amber-500" />
                      )}
                      {item.currencyType === "premium" ? "Premium" : "Regular"}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.isActive ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                        <Check className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800">
                        <X className="h-3 w-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="banners">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-bold">Free Banners</h2>
              <p className="text-sm text-muted-foreground">
                Manage profile banner options
              </p>
            </div>
            <Button onClick={() => setIsBannerDialogOpen(true)}>
              <ImagePlus className="h-4 w-4 mr-2" />
              Add Banner
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Preview</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Shop Item</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bannersQuery.isSuccess && bannersQuery.data?.map((banner: Banner) => (
                <TableRow key={banner.id}>
                  <TableCell>
                    <img
                      src={banner.imageUrl}
                      alt={banner.name}
                      className="w-24 h-12 rounded object-cover"
                    />
                  </TableCell>
                  <TableCell>{banner.name}</TableCell>
                  <TableCell>{banner.category}</TableCell>
                  <TableCell>
                    {banner.shopItemId ? `#${banner.shopItemId}` : "None"}
                  </TableCell>
                  <TableCell>
                    {banner.isActive ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                        <Check className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800">
                        <X className="h-3 w-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit("banner", banner)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete("banner", banner.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="shopBanners">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-bold">Shop Banners</h2>
              <p className="text-sm text-muted-foreground">
                Manage purchasable banner items in the shop
              </p>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Preview</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shopBanners?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="w-24 h-12 rounded object-cover"
                      />
                    )}
                  </TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.price}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {item.currencyType === "premium" ? (
                        <Gem className="h-4 w-4 text-purple-500" />
                      ) : (
                        <Coins className="h-4 w-4 text-amber-500" />
                      )}
                      {item.currencyType === "premium" ? "Premium" : "Regular"}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.isActive ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                        <Check className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800">
                        <X className="h-3 w-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>
      </Tabs>

      {/* Create Avatar Dialog */}
      <Dialog open={isAvatarDialogOpen} onOpenChange={setIsAvatarDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Avatar</DialogTitle>
            <DialogDescription>
              Create a new avatar option for users
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateAvatar}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="avatar-name">Name</Label>
                <Input
                  id="avatar-name"
                  value={avatarFormData.name}
                  onChange={(e) =>
                    setAvatarFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="avatar-category">Category</Label>
                <Input
                  id="avatar-category"
                  value={avatarFormData.category}
                  onChange={(e) =>
                    setAvatarFormData((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="avatar-image">Image</Label>
                <Input
                  id="avatar-image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "avatar")}
                  required
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="avatar-active"
                  checked={avatarFormData.isActive}
                  onCheckedChange={(checked) =>
                    setAvatarFormData((prev) => ({
                      ...prev,
                      isActive: checked,
                    }))
                  }
                />
                <Label htmlFor="avatar-active">Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAvatarDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createAvatarMutation.isPending}
              >
                {createAvatarMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Avatar"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Banner Dialog */}
      <Dialog open={isBannerDialogOpen} onOpenChange={setIsBannerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Banner</DialogTitle>
            <DialogDescription>
              Create a new profile banner option
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateBanner}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="banner-name">Name</Label>
                <Input
                  id="banner-name"
                  value={bannerFormData.name}
                  onChange={(e) =>
                    setBannerFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="banner-category">Category</Label>
                <Input
                  id="banner-category"
                  value={bannerFormData.category}
                  onChange={(e) =>
                    setBannerFormData((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="banner-shop-item">Shop Item ID (Optional)</Label>
                <Input
                  id="banner-shop-item"
                  type="number"
                  value={bannerFormData.shopItemId}
                  onChange={(e) =>
                    setBannerFormData((prev) => ({
                      ...prev,
                      shopItemId: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="banner-image">Image</Label>
                <Input
                  id="banner-image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "banner")}
                  required
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="banner-active"
                  checked={bannerFormData.isActive}
                  onCheckedChange={(checked) =>
                    setBannerFormData((prev) => ({
                      ...prev,
                      isActive: checked,
                    }))
                  }
                />
                <Label htmlFor="banner-active">Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsBannerDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createBannerMutation.isPending}
              >
                {createBannerMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Banner"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Item</DialogTitle>
            <DialogDescription>
              Edit the selected item
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editingItem?.item.name ?? ""}
                  onChange={(e) => {
                    if (editingItem?.type === "avatar") {
                      setEditingItem((prev) => ({
                        type: prev!.type,
                        item: { ...prev!.item, name: e.target.value } as Avatar,
                      }));
                    } else {
                      setEditingItem((prev) => ({
                        type: prev!.type,
                        item: { ...prev!.item, name: e.target.value } as Banner,
                      }));
                    }
                  }}
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-category">Category</Label>
                <Input
                  id="edit-category"
                  value={editingItem?.item.category ?? ""}
                  onChange={(e) => {
                    if (editingItem?.type === "avatar") {
                      setEditingItem((prev) => ({
                        type: prev!.type,
                        item: { ...prev!.item, category: e.target.value } as Avatar,
                      }));
                    } else {
                      setEditingItem((prev) => ({
                        type: prev!.type,
                        item: { ...prev!.item, category: e.target.value } as Banner,
                      }));
                    }
                  }}
                />
              </div>

              {editingItem?.type === "banner" && (
                <div className="grid gap-2">
                  <Label htmlFor="edit-shop-item">Shop Item ID (Optional)</Label>
                  <Input
                    id="edit-shop-item"
                    type="number"
                    value={(editingItem.item as Banner).shopItemId ?? ""}
                    onChange={(e) => {
                      setEditingItem((prev) => ({
                        type: prev!.type,
                        item: { ...prev!.item, shopItemId: parseInt(e.target.value) } as Banner,
                      }));
                    }}
                  />
                </div>
              )}

              <div className="grid gap-2">
                <Label htmlFor="edit-image">Image</Label>
                <Input
                  id="edit-image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, editingItem!.type)}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-active"
                  checked={editingItem?.item.isActive || false}
                  onCheckedChange={(checked) => {
                    if (editingItem?.type === "avatar") {
                      setEditingItem((prev) => ({
                        type: prev!.type,
                        item: { ...prev!.item, isActive: checked } as Avatar,
                      }));
                    } else {
                      setEditingItem((prev) => ({
                        type: prev!.type,
                        item: { ...prev!.item, isActive: checked } as Banner,
                      }));
                    }
                  }}
                />
                <Label htmlFor="edit-active">Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={editItemMutation.isPending}
              >
                {editItemMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Item"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the item. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteItemMutation.isPending ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}